import { Controller, FormProvider } from 'react-hook-form';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import styles from './ProductNumber.module.css';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Input, Tooltip } from '@dsi/react-eds';
import { useState, useEffect } from 'react';
import EditProductNumberDialog from './EditProductNumberDialog';
import DeleteProductNumberDialog from './DeleteProductNumberDialog';
import { useProductNumberForm, productNumberDefaultValues } from './use-product-number-form';
import ProductNumberTable from './ProductNumberTable';
import api from '../../../api/axiosInstance';
import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useLoader } from '../../../hooks/useLoader';

const ProductNumberUI: React.FC= () => {
  const { addNotifications } = useSwobNotifications();
  const methods = useProductNumberForm();
  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = methods;
  const [productNumbers, setProductNumbers] = useState<{ name: string; sw_product_no: string; release_note_no?: string; product_header?: string; webhook?: string; }[]>([]);

  //const [editIndex, setEditIndex] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [filterNumber, setFilterNumber] = useState('');
  const [filterName, setFilterLine] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<{ sw_product_no: string; name: string } | null>(null);
  const [deleteProduct, setDeleteProduct] = useState<{ sw_product_no: string;name: string } | null>(null);
  const [editDialogIndex, setEditDialogIndex] = useState<number | null>(null);
  const [deleteDialogIndex, setDeleteDialogIndex] = useState<number | null>(null);
  const { showLoader, hideLoader } = useLoader();

  const [sortAttr, setSortAttr] = useState<'sw_product_no' | 'name' | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);
  const [pageSize, setPageSize] = useState('10');

  const swgwProductData = async () => {
    try {
      showLoader();

      const resp = await api.get('/api/swgw-product');
      setProductNumbers(resp.data.products);
      return resp;
    } catch (error: unknown) {
      setProductNumbers([]);
      // show error
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching Product Number & Software Categories for test collections.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    } finally {
      hideLoader();
    }
  };
  useQuery({ queryKey: ['swgwProduct'], queryFn: swgwProductData, retry: 0, refetchOnWindowFocus: false });

  const filteredProductNumbers = productNumbers ? productNumbers?.filter((pn) => {
    const matchesNumber = filterNumber === '' || pn.sw_product_no.includes(filterNumber);
    const matchesLine = filterName === '' || pn.name?.toLowerCase()?.includes(filterName.toLowerCase());
    return matchesNumber && matchesLine;
  }) : []

  const sortedProductNumbers = [...filteredProductNumbers].sort((a, b) => {
    if (!sortAttr || !sortOrder) return 0;
    const aVal = a[sortAttr] || '';
    const bVal = b[sortAttr] || '';
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedProductNumbers = sortedProductNumbers.slice((page - 1) * parseInt(pageSize, 10), page * parseInt(pageSize, 10));
  const numPages = Math.max(1, Math.ceil(sortedProductNumbers.length / parseInt(pageSize, 10)));

  useEffect(() => {
    if (page > numPages) {
      setPage(numPages);
    }
  }, [productNumbers, numPages, page]);

  const onSubmit = async (data: { sw_product_no: string; name: string }) => {
    const isDuplicate = productNumbers.some((u) => u.sw_product_no === data.sw_product_no && u.name === data.name);
    if (isDuplicate) {
      addNotifications([
        {
          title: 'Warning - SWGW Product Number & SW Categories',
          icon: 'icon-triangle-warning',
          description: `Product ${data.sw_product_no} - ${data.name} already exists.`,
          timestamp: new Date(),
          color: 'grey'
        }
      ]);
      return;
    }
    try {
      showLoader();
      const resp = await api.post('/api/swgw-product', {
        products: [{
          name: data.name,
          sw_product_no: data.sw_product_no
        }] });
      reset(productNumberDefaultValues);
      swgwProductData();
      // Only handles one product
      if (resp.data.failedProducts.length > 0) {
        addNotifications([
          {
            title: 'Error - SWGW Product Number & SW Categories',
            icon: 'icon-triangle-warning',
            description: `${resp.data?.failedProducts[0]?.error}` ||`Failed to add Product ${data.sw_product_no}`,
            timestamp: new Date(),
            color: 'red'
          }
        ]);

      } else {
        addNotifications([
          {
            title: 'Success- SWGW Product Number & SW Categories',
            icon: 'icon-success',
            description: `Product ${data.sw_product_no} was added successfully.`,
            timestamp: new Date(),
            color: 'green'
          }
        ]);
      }

      return resp;
    } catch (error: unknown) {
      addNotifications([
        {
          title: 'Error - Product Number & SW Categories',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching Product Number & SW Categories',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      hideLoader();
    }
  };

  const handleEdit = (idx: number) => {
    setEditDialogIndex(idx);
    setEditProduct(productNumbers[idx]);
    setEditDialogOpen(true);
  };

  const handleDelete = (idx: number) => {
    setDeleteDialogIndex(idx);
    setDeleteProduct(productNumbers[idx]);
    setDeleteDialogOpen(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.settingsTitleContainer}>
        <div className={styles.settingsTitle}>Product Number & SW Categories</div>
        <p className={styles.productDescription}>Manage product numbers associated with software categories.</p>
      </div>
      <div className="row">
        <div className={styles.addProduct}>
          <FormProvider {...methods}>
            <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <div className={`${styles.settingsTitle} ${styles.whiteText}`}>Add Product Number</div>
                <div className={styles.formRow}>
                  <div>
                    <label htmlFor="product-number" className={styles.whiteText}>
                      Product Number
                      <span className={styles.requiredAsterisk}> *</span>{' '}
                      <ReactEDSWrapper component={Tooltip} message="Unique identifier assigned to the product being onboarded." pointer>
                        <span>
                          <i className={`icon icon-info ${styles.infoIcon}`}></i>
                        </span>
                      </ReactEDSWrapper>
                    </label>
                    <Controller
                      name="sw_product_no"
                      control={control}
                      render={({ field }) => (
                        <ReactEDSWrapper
                          component={Input}
                          id="product-number"
                          type="text"
                          placeholder="Product number"
                          className={
                            (errors.sw_product_no ? `${styles.inputField} ${styles.inputError}` : styles.inputField) + ' ' + styles.emailInputWide
                          }
                          {...field}
                        />
                      )}
                    />
                    <div className={styles.minHeight24}>
                      {errors.sw_product_no ? (
                        <span className={styles.error}>
                          <span className={styles.dangerIcon} aria-label="error" role="img">
                            &#9888;
                          </span>
                          {typeof errors.sw_product_no.message === 'string' ? errors.sw_product_no.message : 'Required'}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="product-line" className={styles.whiteText}>
                      Description
                      <span className={styles.requiredAsterisk}> *</span>{' '}
                      <ReactEDSWrapper
                        component={Tooltip}
                        message="Specify the description associated with this onboarding configuration."
                        pointer
                      >
                        <span>
                          <i className={`icon icon-info ${styles.infoIcon}`}></i>
                        </span>
                      </ReactEDSWrapper>
                    </label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <ReactEDSWrapper
                          component={Input}
                          id="product-name"
                          type="text"
                          placeholder="Description"
                          className={
                            (errors.name ? `${styles.inputField} ${styles.inputError}` : styles.inputField) + ' ' + styles.emailInputWide
                          }
                          {...field}
                        />
                      )}
                    />
                    <div className={styles.minHeight24}>
                      {errors.name ? (
                        <span className={styles.error}>
                          <span className={styles.dangerIcon} aria-label="error" role="img">
                            &#9888;
                          </span>
                          {typeof errors.name.message === 'string' ? errors.name.message : 'Required'}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.flexColStart}>
                    <label className={styles.invisibleLabel}>&nbsp;</label>
                    <ReactEDSWrapper component={Tooltip} message="Click to add the product number with the specified product line." pointer>
                      <span>
                        <ReactEDSWrapper component={Button} variant="secondary" className={`btn secondary ${styles.addButton}`} type="submit"
                          onClick={handleSubmit(onSubmit)}>
                          <i className="icon icon-plus" /> Add
                        </ReactEDSWrapper>
                      </span>
                    </ReactEDSWrapper>
                    <div className={styles.minHeight24}></div>
                  </div>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
        <div className={styles.vl2} ></div>
        <div className={styles.vl} >
        { productNumbers.length > 0 ? (
          <ProductNumberTable
            productNumbers={paginatedProductNumbers}
            page={page}
            pageSize={pageSize}
            numPages={numPages}
            filterNumber={filterNumber}
            filterName={filterName}
            sortAttr={sortAttr}
            sortOrder={sortOrder}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSort={(order, attr) => {
              setSortAttr(attr);
              setSortOrder(order);
            }}
            onFilterNumber={(val) => {
              setFilterNumber(val);
              setPage(1);
            }}
            onFilterLine={(val) => {
              setFilterLine(val);
              setPage(1);
            }}
          />
        ) : (
          <div className={styles.emptyState}>
            <i className="icon icon-info" />
            <div className={styles.emptyStateTitle}>There are no product numbers</div>
          </div>
        )}
        </div>
        <EditProductNumberDialog
          open={editDialogOpen}
          initialProduct={editProduct}
          onSave={(data) => {
            if (editDialogIndex !== null) {
              setProductNumbers((productNumbers) => productNumbers.map((u, i) => (i === editDialogIndex ? data : u)));
            }
            setEditDialogOpen(false);
            setEditDialogIndex(null);
            setEditProduct(null);
            // Melodie Check if we can update
            swgwProductData();
          }}
          onClose={() => {
            setEditDialogOpen(false);
            setEditDialogIndex(null);
            setEditProduct(null);
          }}
        />
        <DeleteProductNumberDialog
          open={deleteDialogOpen}
          product={deleteProduct}
          onDelete={() => {
            if (deleteDialogIndex !== null) {
              setProductNumbers((productNumbers) => productNumbers.filter((_, i) => i !== deleteDialogIndex));
            }
            setDeleteDialogOpen(false);
            setDeleteDialogIndex(null);
            setDeleteProduct(null);
          }}
          onCancel={() => {
            setDeleteDialogOpen(false);
            setDeleteDialogIndex(null);
            setDeleteProduct(null);
          }}
        />
      </div>
    </div>
  );
}
export default ProductNumberUI;
